import { FunctionComponent, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import {
	countIncluded,
	CreditConfirmModal,
	quoteSegmentViews,
} from "@/components/CreditConfirmModal";
import { Toggle } from "@/components/Toggle/Toggle";
import { useGetCreditQuoteQuery } from "@/pages/Private/redux/list/creditQuote.api";

import { BulkContactInfoRequest } from "../../utils/bulkContactInfoRequest";

interface ModalBulkLeadScoringProps {
	/** The selection the retrigger will send, quoted as it is. */
	request: BulkContactInfoRequest;
	/** Leads selected, as the table counts them, until the quote answers. */
	selectedCount: number;
	onClose: () => void;
	/** Runs the retrigger; rejects when the API refuses it. */
	onConfirm: (rescore: boolean) => Promise<unknown>;
}

/**
 * "Retrigger lead scoring" on the credit-confirm modal (PREC-4263). Unscored leads are scored for
 * free; already scored ones are rescored, at RESCORE_LEAD_CREDITS each, only with the toggle on,
 * and the cost appears with it. The split is the retrigger's own, quoted by the server for the
 * whole selection. Mounted while open, so the toggle starts off each time.
 */
export const ModalBulkLeadScoring: FunctionComponent<ModalBulkLeadScoringProps> = ({
	request,
	selectedCount,
	onClose,
	onConfirm,
}) => {
	const { t } = useTranslation();
	const [rescore, setRescore] = useState(false);
	const quote = useGetCreditQuoteQuery(
		{ ...request, action: "RESCORE" },
		{ refetchOnMountOrArgChange: true }
	);
	const segments = quote.data ? quoteSegmentViews(quote.data, t, { rescore }) : undefined;
	const selected = quote.data?.selected ?? selectedCount;
	const noConfig =
		quote.error && "status" in quote.error && quote.error.status === 404
			? t("inbox.bulkLeadScoringNoConfig")
			: undefined;

	return (
		<CreditConfirmModal
			isOpened
			body={
				<Trans
					components={{ b: <b className="font-semibold" /> }}
					i18nKey="creditConfirm.rescore.body"
					values={{ target: t("creditConfirm.selectedLeads", { count: selected }) }}
				/>
			}
			confirmLabel={t("creditConfirm.rescore.confirm", {
				count: segments ? countIncluded(segments) : selected,
			})}
			nothingChargedOnRefusal={false}
			quoteError={quote.error}
			quoteErrorMessage={noConfig}
			segments={segments}
			title={t("creditConfirm.rescore.title")}
			onClose={onClose}
			onConfirm={() => onConfirm(rescore)}
		>
			<div className="flex items-start gap-3">
				<Toggle isChecked={rescore} testId="rescore-toggle" onChange={() => setRescore(!rescore)} />
				<div className="flex flex-col">
					<span className="text-sm text-text">{t("creditConfirm.rescore.toggle")}</span>
					<span className="text-xs text-text-meta">{t("creditConfirm.rescore.toggleHint")}</span>
				</div>
			</div>
		</CreditConfirmModal>
	);
};
